import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/solid";
import { Fragment, useMemo } from "react";
import toast from "react-hot-toast";

function getDeptId(t) {
    return t?.departmentId ?? t?.deparmentId ?? "__no_department__";
}

function getDeptName(t) {
    return (
        t?.departmentName ??
        t?.deparmentName ??
        (getDeptId(t) === "__no_department__"
            ? "No department"
            : "Unknown department")
    );
}

const AssignTeamsListBox = ({ title, teams, assignedTeams, onChange }) => {
    console.log("AssignTeamsListBox render", assignedTeams);
    // Group teams by department
    const departmentGroups = useMemo(() => {
        const map = new Map();

        (teams || []).forEach((t) => {
            const depId = getDeptId(t);
            const depName = getDeptName(t);

            if (!map.has(depId)) map.set(depId, { name: depName, items: [] });
            map.get(depId).items.push(t);
        });

        return Array.from(map.entries()).map(([id, group]) => ({
            id,
            name: group.name,
            items: group.items.sort((a, b) =>
                (a.name || "").localeCompare(b.name || "")
            ),
        }));
    }, [teams]);

    // Manual selection controller (not HeadlessUI)
    const handleSelect = (team) => {
        const deptId = getDeptId(team);

        const alreadyInDept = assignedTeams.some(
            (t) => getDeptId(t) === deptId && t.id !== team.id
        );

        if (alreadyInDept) {
            toast.error("Only one team per department is allowed.");
            return;
        }

        const exists = assignedTeams.some((t) => t.id === team.id);

        if (exists) {
            // remove
            onChange(assignedTeams.filter((t) => t.id !== team.id));
        } else {
            // add
            onChange([...assignedTeams, team]);
        }
    };

    const selectedDeptIds = new Set(assignedTeams.map((t) => getDeptId(t)));

    // Button label
    const selectedLabel =
        assignedTeams.length > 0
            ? assignedTeams.map((t) => t.name).join(", ")
            : "Select teams";

    return (
        <div className="mt-5 sm:mt-6">
            <label className="block text-sm font-medium text-gray-700">
                {title}
            </label>

            {/* No value, no multiple, no onChange -> fully controlled by us */}
            <Listbox as="div" className="relative inline-block w-full text-left">
                <div>
                    <Listbox.Button className="inline-flex justify-between w-full px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50">
                        <span className="truncate">{selectedLabel}</span>
                        <ChevronUpDownIcon className="w-5 h-5" />
                    </Listbox.Button>
                </div>

                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Listbox.Options className="absolute z-10 w-full mt-2 overflow-auto bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-72">
                        {departmentGroups.map((group) => (
                            <li key={group.id}>
                                {/* Group label */}
                                <div className="sticky top-0 bg-slate-50 px-3 py-1.5 text-xs font-semibold uppercase text-slate-600 border-b border-slate-200">
                                    {group.name}
                                </div>

                                {/* Items */}
                                {group.items.map((team) => {
                                    const depId = getDeptId(team);
                                    const isSelected = assignedTeams.some(
                                        (t) => t.id === team.id
                                    );
                                    const isBlocked =
                                        selectedDeptIds.has(depId) && !isSelected;

                                    return (
                                        <div
                                            key={team.id}
                                            className={`px-2 py-2 pl-8 pr-4 ${
                                                isBlocked
                                                    ? "opacity-50 cursor-not-allowed"
                                                    : "cursor-pointer hover:bg-blue-100"
                                            }`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (isBlocked) {
                                                    toast.error(
                                                        "You can only select one team per department."
                                                    );
                                                    return;
                                                }

                                                handleSelect(team);
                                            }}
                                        >
                                            {isSelected && (
                                                <span className="absolute left-0 pl-2 text-blue-600">
                                                    <CheckIcon className="w-5 h-5" />
                                                </span>
                                            )}

                                            <div className="flex flex-col">
                                                <span
                                                    className={`${
                                                        isSelected
                                                            ? "font-medium"
                                                            : ""
                                                    }`}
                                                >
                                                    {team.name}
                                                </span>
                                                <span className="text-xs text-slate-500">
                                                    {getDeptName(team)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </li>
                        ))}
                    </Listbox.Options>
                </Transition>
            </Listbox>
        </div>
    );
};

export default AssignTeamsListBox;
